// src/pages/CourseTime/styles/activity.styles.js
import styled, { keyframes } from 'styled-components';
import { PHONE_LANDSCAPE } from './slides.styles';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Floating Activity Button (Magenta theme)
export const FloatingActivityButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 100px; /* Position to the left of chat button */
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e91e63, #ad1457);
  border: none;
  box-shadow: 0 4px 20px rgba(233, 30, 99, 0.3);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(233, 30, 99, 0.4);
    animation: ${pulse} 2s infinite;
    background: linear-gradient(135deg, #f06292, #e91e63);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    animation: none;
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    bottom: 20px;
    right: 88px; /* Adjust for mobile */

    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media ${PHONE_LANDSCAPE} {
    width: 44px;
    height: 44px;
    bottom: 12px;
    right: 64px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

// Activity Modal Overlay
export const ActivityModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(4px);
  font-family: "Source Sans Pro", "Helvetica Neue";
`;

// Activity Modal Container
export const ActivityModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  height: 85vh;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 90vh;
    max-height: none;
    border-radius: 12px;
    margin: 10px;
    max-width: calc(100vw - 20px);
  }
`;

// Activity Modal Header
export const ActivityModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #ffffff 0%, ${props => props.themeColor ? `${props.themeColor}15` : '#fce4ec'} 50%, ${props => props.themeColor ? `${props.themeColor}25` : '#f8bbd9'} 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 24px;
    right: 24px;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${props => props.themeColor || '#e91e63'}, transparent);
  }
`;

export const ActivityModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.themeColor || '#e91e63'};
  margin: 0;
  font-family: "Source Sans Pro", "Helvetica Neue";
  letter-spacing: -0.02em;
  text-align: center;
  flex: 1;
`;

export const ActivityCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.themeColor ? `${props.themeColor}1a` : 'rgba(233, 30, 99, 0.1)'};
    color: ${props => props.themeColor || '#e91e63'};
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }
  
  &:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: rotate(90deg) scale(1.1);
  }
`;

// Activity Tabs Container
export const ActivityTabsContainer = styled.div`
  display: flex;
  background-color: #fff;
  padding: 16px 20px 12px;
  gap: 12px;
  overflow-x: auto;
  border-bottom: 1px solid #f1f5f9;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.themeColor || '#e91e63'};
    border-radius: 2px;
  }
`;

export const ActivityTab = styled.button`
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? (props.themeColor || '#e91e63') : '#e2e8f0'};
  background: ${props => {
    if (props.active) {
      const color = props.themeColor || '#e91e63';
      const darkerColor = props.themeColor ? `${props.themeColor}cc` : '#ad1457';
      return `linear-gradient(135deg, ${color}, ${darkerColor})`;
    }
    return 'linear-gradient(135deg, #ffffff, #f8fafc)';
  }};
  color: ${props => props.active ? '#fff' : '#64748b'};
  font-size: 14px;
  font-weight: ${props => props.active ? '700' : '600'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  min-width: auto;
  border-radius: 28px;
  letter-spacing: -0.01em;
  box-shadow: ${props => {
    if (props.active) {
      const color = props.themeColor || '#e91e63';
      return `0 4px 12px ${color}40, 0 2px 4px ${color}26`;
    }
    return '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)';
  }};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: ${props => {
      if (props.active) {
        const color = props.themeColor || '#e91e63';
        const lighterColor = props.themeColor ? `${props.themeColor}dd` : '#f06292';
        return `linear-gradient(135deg, ${lighterColor}, ${color})`;
      }
      return 'linear-gradient(135deg, #f1f5f9, #e2e8f0)';
    }};
    border-color: ${props => props.active ? (props.themeColor || '#e91e63') : '#cbd5e1'};
    color: ${props => props.active ? '#fff' : '#374151'};
    transform: translateY(-2px) scale(1.02);
    box-shadow: ${props => {
      if (props.active) {
        const color = props.themeColor || '#e91e63';
        return `0 6px 20px ${color}59, 0 3px 8px ${color}33`;
      }
      return '0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06)';
    }};
    
    &::before {
      left: 100%;
    }
  }
  
  &:focus {
    outline: 2px solid ${props => props.themeColor || '#e91e63'};
    outline-offset: 2px;
  }
  
  &:active {
    transform: translateY(-1px) scale(1.01);
    transition: all 0.1s ease;
  }
`;

// Activity Content Area
export const ActivityContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const ActivityTabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Activity Responses Container
export const ActivityResponsesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => {
      const color = props.themeColor || '#e91e63';
      return `
        radial-gradient(circle at 20% 20%, ${color}08 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, ${color}08 0%, transparent 50%),
        radial-gradient(circle at 40% 60%, ${color}05 0%, transparent 50%)
      `;
    }};
    pointer-events: none;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.themeColor || '#e91e63'};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.themeColor ? `${props.themeColor}cc` : '#ad1457'};
  }
`;

// Activity Response Item
export const ActivityResponseItem = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.3s ease-out;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.themeColor || '#e91e63'};
  }
`;

export const ResponseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const ResponseUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ResponseUserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => {
    const color = props.themeColor || '#e91e63';
    const darkerColor = props.themeColor ? `${props.themeColor}cc` : '#ad1457';
    return `linear-gradient(135deg, ${color}, ${darkerColor})`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

export const ResponseUserName = styled.span`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  font-family: 'Source Sans Pro', sans-serif;
`;

export const ResponseTime = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

export const ResponseContent = styled.div`
  color: #374151;
  line-height: 1.5;
  font-size: 14px;
  font-family: 'Source Sans Pro', sans-serif;
`;

// Text Response Styles
export const TextResponseContent = styled.div`
  background: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid ${props => props.themeColor || '#e91e63'};
  font-style: italic;
  color: #374151;
`;

// Poll Response Styles
export const PollResponseContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PollQuestion = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

export const PollAnswer = styled.div`
  background: ${props => {
    const color = props.themeColor || '#e91e63';
    const darkerColor = props.themeColor ? `${props.themeColor}cc` : '#ad1457';
    return `linear-gradient(135deg, ${color}, ${darkerColor})`;
  }};
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
`;

export const PollMultipleAnswers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

// Poll Results Chart Container
export const PollResultsContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

export const PollResultsTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.themeColor || '#e91e63'};
`;

export const PollOptionResult = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
`;

export const PollOptionText = styled.span`
  min-width: 120px;
  font-size: 13px;
  color: #374151;
`;

export const PollOptionBar = styled.div`
  flex: 1;
  height: 20px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

export const PollOptionFill = styled.div`
  height: 100%;
  background: ${props => {
    const color = props.themeColor || '#e91e63';
    const darkerColor = props.themeColor ? `${props.themeColor}cc` : '#ad1457';
    return `linear-gradient(135deg, ${color}, ${darkerColor})`;
  }};
  width: ${props => props.percentage}%;
  transition: width 0.5s ease;
  border-radius: 10px;
`;

export const PollOptionPercentage = styled.span`
  min-width: 40px;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.themeColor || '#e91e63'};
`;

// Loading and Empty States
export const ActivityLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6b7280;
  font-size: 14px;
`;

export const ActivityLoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid ${props => props.themeColor || '#e91e63'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 12px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const ActivityEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
`;

export const ActivityEmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.themeColor ? `${props.themeColor}15` : '#fce4ec'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  
  svg {
    width: 32px;
    height: 32px;
    color: ${props => props.themeColor || '#e91e63'};
  }
`;

export const ActivityEmptyStateText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #374151;
`;

export const ActivityEmptyStateSubtext = styled.p`
  font-size: 14px;
  margin: 0;
  color: #6b7280;
`;

// Error State
export const ActivityErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #dc2626;
`;

export const ActivityErrorText = styled.p`
  font-size: 14px;
  margin: 0;
`;
